import { useId, useMemo, useState } from "react";
import { SlideOutPanel, useSlideOut } from "./SlideOutPanel";
import {
  EGGER_DECORS,
  EGGER_DECOR_GROUPS,
  type EggerDecor,
  type EggerDecorGroup
} from "../domain/eggerDecors";
import { decorTextureUrl, resolveDecor, searchDecors } from "../domain/decors";
import "./DecorPicker.css";

const GROUPS = Object.keys(EGGER_DECOR_GROUPS) as EggerDecorGroup[];

/**
 * Выбор декора Egger: 151 позиция, поэтому без фильтра по группе и поиска
 * по названию или артикулу список бесполезен. Образец однотонного декора
 * рисуется цветом — текстуры у него нет и не должно быть.
 */
export function DecorPicker({ value, onChange, label }: {
  value: string | undefined;
  onChange: (code: string) => void;
  label: string;
}) {
  const current = resolveDecor(value);
  const [group, setGroup] = useState<EggerDecorGroup | "all">("all");
  const [query, setQuery] = useState("");

  const found = useMemo(
    () => searchDecors(query, group === "all" ? undefined : group),
    [query, group]
  );

  const sample = (decor: EggerDecor) => {
    const url = decorTextureUrl(decor);
    return url
      ? { backgroundImage: `url(${url})`, backgroundSize: "cover", backgroundPosition: "center" }
      : { backgroundColor: decor.hex };
  };

  const owner = `decor:${useId()}`;
  const isOpen = useSlideOut(state => state.owner === owner);
  const openPanel = useSlideOut(state => state.open);
  const closePanel = useSlideOut(state => state.close);

  // В боковой панели — только выбранный декор; каталог из 151 позиции
  // открывается в высокой панели слева, где видно много образцов сразу.
  return (
    <div className="decor-picker">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{label}</span>
          <h3>{current.name}</h3>
          <p className="decor-picker__code">{current.code}</p>
        </div>
        <span className="decor-picker__chip" style={sample(current)} aria-hidden="true" />
      </div>
      <button type="button" className="decor-picker__open" aria-expanded={isOpen}
        onClick={() => (isOpen ? closePanel(owner) : openPanel(owner))}>
        <span><strong>{isOpen ? "Каталог открыт слева" : "Выбрать из каталога"}</strong>
          <small>{EGGER_DECORS.length} декоров Egger · поиск по названию и артикулу</small></span>
        <i aria-hidden="true">{isOpen ? "›" : "‹"}</i>
      </button>

      <SlideOutPanel owner={owner} eyebrow={label} title="Каталог декоров Egger">
        <div className="decor-picker decor-picker--catalog">
          <div className="decor-picker__current">
            <span className="decor-picker__chip" style={sample(current)} aria-hidden="true" />
            <span><small>Сейчас выбран</small><strong>{current.name}</strong><em>{current.code}</em></span>
          </div>
          <label className="decor-picker__search">
            <input
              type="search"
              value={query}
              placeholder="Название или артикул"
              aria-label="Поиск декора"
              onChange={event => setQuery(event.target.value)}
            />
          </label>

          <div className="decor-picker__groups">
            <button type="button" aria-pressed={group === "all"} onClick={() => setGroup("all")}>
              Все <small>{EGGER_DECORS.length}</small>
            </button>
            {GROUPS.map(key => {
              const count = EGGER_DECORS.filter(decor => decor.group === key).length;
              return (
                <button key={key} type="button" aria-pressed={group === key} onClick={() => setGroup(key)}>
                  {EGGER_DECOR_GROUPS[key]} <small>{count}</small>
                </button>
              );
            })}
          </div>

          {found.length === 0 ? (
            <p className="soft-note">Ничего не нашлось. Попробуйте артикул целиком, например «H1145 ST10».</p>
          ) : (
            <div className="decor-picker__grid">
              {found.map(decor => (
                <button
                  key={decor.code}
                  type="button"
                  className={`decor-card${decor.code === current.code ? " is-selected" : ""}`}
                  aria-pressed={decor.code === current.code}
                  title={`${decor.name} · ${decor.code}`}
                  onClick={() => onChange(decor.code)}
                >
                  <span className="decor-card__sample" style={sample(decor)} aria-hidden="true" />
                  <strong>{decor.name}</strong>
                  <small>{decor.code}</small>
                </button>
              ))}
            </div>
          )}
          <p className="decor-picker__total">Показано {found.length} из {EGGER_DECORS.length}</p>
        </div>
      </SlideOutPanel>
    </div>
  );
}
